import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaidIcon from '@mui/icons-material/Paid';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { JournalSummary as JournalSummaryData } from '../../../types/ledgerJournal';
import { eventColor, formatDate, formatRange, formatSar, humanizeEvent } from '../journalExplorer.helpers';

interface JournalSummaryProps {
  summary?: JournalSummaryData;
  book?: string;
  /** The searched date range — shown as the period, with the journals' own dates under it. */
  range: { from: Date | null; to: Date | null };
  loading: boolean;
  activeEvent: string;
  onEventClick: (event: string) => void;
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
  loading: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, hint, loading }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} noWrap>
        {loading ? <Skeleton width={120} /> : value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
  </Paper>
);

export const JournalSummary: React.FC<JournalSummaryProps> = ({
  summary,
  book,
  range,
  loading,
  activeEvent,
  onEventClick,
}) => {
  const found =
    summary?.firstAt && summary?.lastAt
      ? `Journals found ${formatDate(summary.firstAt)} → ${formatDate(summary.lastAt)}`
      : 'No journals in this range';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <StatCard
          icon={<ReceiptLongIcon />}
          label="Journals"
          value={(summary?.journalCount ?? 0).toLocaleString('en-US')}
          hint={book ? `Book ${book}` : undefined}
          loading={loading}
        />
        <StatCard
          icon={<PaidIcon />}
          label="Total amount"
          value={formatSar(summary?.totalAmount ?? 0)}
          hint="Sum of the debit side"
          loading={loading}
        />
        <StatCard
          icon={<DateRangeIcon />}
          label="Period"
          value={formatRange(range.from, range.to)}
          hint={found}
          loading={loading}
        />
      </Box>

      {!!summary?.byEvent.length && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            By event
          </Typography>
          {summary.byEvent.map(({ event, count, amount }) => {
            const active = activeEvent === event;

            return (
              <Chip
                key={event}
                size="small"
                color={eventColor(event)}
                variant={active ? 'filled' : 'outlined'}
                label={`${humanizeEvent(event)} · ${count}`}
                title={formatSar(amount)}
                onClick={() => onEventClick(active ? '' : event)}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};
